import React, { useState, useEffect, useRef } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

const AnimatedStat: React.FC<{ value: number; label: string; suffix?: string; duration?: number }> = ({ value, label, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(ref, { threshold: 0.5 });
  
  useEffect(() => {
    if (isVisible) {
      let start = 0;
      const end = value;
      if (start === end) return;

      const totalFrames = duration / (1000 / 60);
      const increment = end / totalFrames;

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.ceil(start));
        }
      }, 1000 / 60);

      return () => clearInterval(timer);
    }
  }, [isVisible, value, duration]);
  
  return (
    <div ref={ref} className="text-center p-4">
      <p className="text-2xl sm:text-3xl md:text-2xl lg:text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)]">
        {count.toLocaleString()}{suffix}
      </p>
      <p className="mt-2 text-sm md:text-base text-[var(--text-secondary)] uppercase tracking-widest">{label}</p>
    </div>
  );
};

const Stats: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(sectionRef, { threshold: 0.1 });

  return (
    <section id="pricing" ref={sectionRef} className={`py-16 sm:py-24 bg-[var(--panel-bg)]/50 transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          <AnimatedStat value={1000000} suffix="+" label="Lines of Code" />
          <AnimatedStat value={500} suffix="+" label="Apps Generated" />
          <AnimatedStat value={99} suffix="%" label="User Satisfaction" />
          <AnimatedStat value={200} suffix="+" label="Happy Developer" />
        </div>
      </div>
    </section>
  );
};

export default Stats;